import { useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Download, Upload } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useRegulatoryAttachmentMutations, useRegulatoryChecklist } from "../hooks";
import { downloadRegulatoryZip } from "../api";
import type { RegulatoryChecklistItem } from "../types";

// Checklist of the attachments the OMI regulatory application requires, per działka:
// which are present (uploaded or auto-sourced from the system — formatka PDF, wypis,
// generated wniosek), which are missing, plus a one-click ZIP of everything available.
export function RegulatoryAttachmentsPanel({
  parcelId,
  connectionId,
}: {
  parcelId: string;
  connectionId: string;
}) {
  const { data, isLoading } = useRegulatoryChecklist(parcelId);
  const m = useRegulatoryAttachmentMutations(parcelId);
  const [zipping, setZipping] = useState(false);
  const [uploadingKind, setUploadingKind] = useState<string | null>(null);

  const onPick = (item: RegulatoryChecklistItem, file?: File) => {
    if (!file) return;
    setUploadingKind(item.kind);
    m.upload.mutate(
      item.level === "project"
        ? { connection: connectionId, kind: item.kind, file }
        : { parcel: parcelId, kind: item.kind, file },
      { onSettled: () => setUploadingKind(null) },
    );
  };

  const zip = async () => {
    setZipping(true);
    try {
      await downloadRegulatoryZip(parcelId);
    } finally {
      setZipping(false);
    }
  };

  if (isLoading) {
    return <div className="py-6 flex justify-center"><Spinner /></div>;
  }

  const items = data?.items ?? [];
  const missing = data?.missing ?? [];
  const anyFiles = items.some((it) => it.files.length > 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="section-label">
          Załączniki do wniosku regulacyjnego{" "}
          {missing.length > 0 ? (
            <span className="text-amber-600 dark:text-amber-400">· brakuje {missing.length}</span>
          ) : (
            <span className="text-green-600 dark:text-green-400">· komplet</span>
          )}
        </p>
        <Button size="sm" variant="secondary" loading={zipping} disabled={!anyFiles} onClick={zip}>
          <Download size={12} /> Pobierz wszystko (ZIP)
        </Button>
      </div>

      <div className="space-y-1.5">
        {items.map((item) => (
          <ChecklistRow
            key={item.kind}
            item={item}
            uploading={uploadingKind === item.kind && m.upload.isPending}
            onPick={(f) => onPick(item, f)}
          />
        ))}
      </div>
    </div>
  );
}

function ChecklistRow({
  item,
  uploading,
  onPick,
}: {
  item: RegulatoryChecklistItem;
  uploading: boolean;
  onPick: (file?: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="border border-[var(--color-border-muted)] p-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-xs font-medium text-[var(--color-text-primary)]">
            {item.present ? (
              <CheckCircle2 size={13} className="text-green-600 dark:text-green-400 shrink-0" />
            ) : (
              <AlertTriangle size={13} className="text-amber-600 dark:text-amber-400 shrink-0" />
            )}
            {item.label}
            {item.source === "auto" && (
              <span className="text-[10px] font-mono uppercase tracking-wide px-1 py-0.5 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] text-[var(--color-text-secondary)]">
                z systemu
              </span>
            )}
            <span className="text-[10px] text-[var(--color-text-muted)]">
              {item.level === "project" ? "projekt" : "działka"}
            </span>
          </div>
          {item.files.length > 0 && (
            <div className="mt-1 flex flex-col gap-0.5">
              {item.files.map((f) =>
                f.url ? (
                  <a
                    key={f.id}
                    href={f.url}
                    download={f.original_filename}
                    className="inline-flex items-center gap-1 text-[11px] font-mono text-[var(--color-primary)] hover:underline truncate"
                  >
                    <Download size={11} /> {f.original_filename}
                  </a>
                ) : null,
              )}
            </div>
          )}
        </div>
        <div className="shrink-0">
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              onPick(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <Button
            size="sm"
            variant="ghost"
            loading={uploading}
            onClick={() => inputRef.current?.click()}
          >
            <Upload size={12} /> Dodaj
          </Button>
        </div>
      </div>
    </div>
  );
}
